import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { foldText, matchesQuery } from '../lib/listSearch';
import { cn, Input, Select } from './ui';

/**
 * The shop / outlet / gate picker of the owner's screens (filter bars, forms and the top bar's outlet switcher).
 *
 * It stays a native <select>, so it keeps working the way people (and screen readers, forms and tests) know, but a
 * native select cannot be typed into on a phone: once the list is long (more than FIND_FROM options) a small find box
 * sits next to it. Typing there narrows the select to the shops whose name or code match, Enter picks the first match
 * and Escape empties the box. The option that is chosen is always kept, so the select never shows an empty value.
 *
 * The options are only ever the ones the page passes in (the login's own outlets or a list the server scoped to it):
 * finding narrows that list, it never adds to it.
 *
 * `compact` (the top bar): a find button instead of the box, opening a small panel with the box and the matches.
 */

export interface PickOption {
  id: string;
  name: string;
  code?: string | null;
}

/** A picker with more options than this gets a find box. */
export const FIND_FROM = 8;

/** The options for a find text: the ones whose name or code match it, plus the one that is chosen. */
export function optionsMatching<T extends PickOption>(options: readonly T[], find: string, chosen: string): T[] {
  if (!foldText(find)) return [...options];
  return options.filter((o) => o.id === chosen || matchesQuery(find, [o.name, o.code]));
}

/** The options that really match a find text (without the chosen one kept for display). */
export function optionHits<T extends PickOption>(options: readonly T[], find: string): T[] {
  return options.filter((o) => matchesQuery(find, [o.name, o.code]));
}

const NO_MATCH = '\u0000no-match';

export function SearchableSelect({
  value,
  onChange,
  options,
  label,
  allLabel,
  id,
  className,
  wrapperClassName,
  compact = false,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly PickOption[];
  /** The select's accessible name ("Shop", "Outlet", "Gate"…), unless a <label for={id}> names it. */
  label?: string;
  /** A first option with the value '' ("All shops", "Not about a shop"…). */
  allLabel?: string;
  id?: string;
  /** Classes of the select itself. */
  className?: string;
  /** Classes of the select and its find box together (only when the list is long enough to have one). */
  wrapperClassName?: string;
  compact?: boolean;
}) {
  const [find, setFind] = useState('');
  const [open, setOpen] = useState(false);
  const hintId = useId();
  const panelId = useId();
  const boxRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const phoneRef = useRef<HTMLButtonElement>(null);
  const searchable = options.length > FIND_FROM;
  const finding = searchable && !!foldText(find);
  const hits = finding ? optionHits(options, find) : options;
  // Inline, the select itself narrows; in the compact panel the matches are listed in the panel instead.
  const shown = searchable && !compact ? optionsMatching(options, find, value) : options;

  const pick = (v: string) => {
    onChange(v);
    setFind('');
    setOpen(false);
  };

  // The compact panel closes on a tap outside it.
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [open]);

  const onFindKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Never submits the form the picker sits in: Enter picks the first match.
      e.preventDefault();
      if (finding && hits[0]) pick(hits[0].id);
    } else if (e.key === 'Escape' && (find || open)) {
      // Empties the box (or closes the panel) without closing a dialog around it.
      e.preventDefault();
      e.stopPropagation();
      if (find) setFind('');
      else {
        setOpen(false);
        // Back to whichever opener is on screen (the phone one or the find button).
        (phoneRef.current?.offsetParent ? phoneRef.current : toggleRef.current)?.focus();
      }
    }
  };

  const select = (
    <Select id={id} aria-label={label} value={value} onChange={(e) => pick(e.target.value)} className={cn(searchable && 'min-w-0 flex-1', className)}>
      {allLabel !== undefined && <option value="">{allLabel}</option>}
      {shown.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
      {finding && !compact && hits.length === 0 && (
        <option value={NO_MATCH} disabled>
          No match for “{find.trim()}”
        </option>
      )}
    </Select>
  );
  if (!searchable) return select;

  const status = (
    <span className="sr-only" aria-live="polite">
      {finding ? (hits.length ? `${hits.length} ${hits.length === 1 ? 'match' : 'matches'}` : `No match for ${find.trim()}`) : ''}
    </span>
  );
  const hint = (
    <span id={hintId} className="sr-only">
      Type part of a name or code. Enter picks the first match.
    </span>
  );

  if (!compact) {
    return (
      <div className={cn('flex min-w-0 max-w-full items-center gap-1.5', wrapperClassName)} data-testid="searchable-select">
        <Input
          type="search"
          aria-label="Narrow the list"
          aria-describedby={hintId}
          placeholder="Find…"
          title="Type part of a name or code"
          value={find}
          maxLength={60}
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="go"
          onChange={(e) => setFind(e.target.value)}
          onKeyDown={onFindKey}
          className={cn('w-24 shrink-0 sm:w-32', finding && hits.length === 0 && 'border-red-300 focus:border-red-400 focus:ring-red-100')}
        />
        {select}
        {hint}
        {status}
      </div>
    );
  }

  // On a phone the top bar has no room for a select and a button: one button shows the outlet and opens the panel.
  const current = options.find((o) => o.id === value)?.name ?? allLabel ?? 'Choose';
  return (
    <div ref={boxRef} className={cn('relative flex min-w-0 items-center gap-1', wrapperClassName)} data-testid="searchable-select">
      <div className="hidden min-w-0 flex-1 sm:block">{select}</div>
      <button
        ref={phoneRef}
        type="button"
        aria-label={`${label ?? 'Choose'}: ${current}. Find another`}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className="flex h-11 min-w-0 flex-1 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 text-left text-sm text-slate-900 sm:hidden"
      >
        <span className="min-w-0 flex-1 truncate">{current}</span>
        <svg aria-hidden viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4 shrink-0 text-slate-500">
          <circle cx="8.5" cy="8.5" r="5.5" />
          <path d="m13 13 4.5 4.5" strokeLinecap="round" />
        </svg>
      </button>
      <button
        ref={toggleRef}
        type="button"
        aria-label="Find in the list"
        aria-expanded={open}
        aria-controls={panelId}
        title="Find by name or code"
        onClick={() => setOpen((v) => !v)}
        className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 sm:flex"
      >
        <svg aria-hidden viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
          <circle cx="8.5" cy="8.5" r="5.5" />
          <path d="m13 13 4.5 4.5" strokeLinecap="round" />
        </svg>
      </button>
      {open && (
        <div id={panelId} className="fixed inset-x-3 top-14 z-50 rounded-lg border border-slate-200 bg-white p-2 shadow-lg sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-1 sm:w-72">
          <Input
            type="search"
            autoFocus
            aria-label="Narrow the list"
            aria-describedby={hintId}
            placeholder="Name or code"
            value={find}
            maxLength={60}
            autoComplete="off"
            spellCheck={false}
            enterKeyHint="go"
            onChange={(e) => setFind(e.target.value)}
            onKeyDown={onFindKey}
          />
          <ul className="mt-1 max-h-72 overflow-y-auto" aria-label="Matches">
            {hits.map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  onClick={() => pick(o.id)}
                  aria-current={o.id === value || undefined}
                  className={cn('flex min-h-11 w-full items-center justify-between gap-2 rounded px-2 text-left text-sm hover:bg-slate-50 lg:min-h-9', o.id === value ? 'font-semibold text-brand-700' : 'text-slate-800')}
                >
                  <span className="min-w-0 truncate">{o.name}</span>
                  {o.code && <span className="shrink-0 font-mono text-xs text-slate-400">{o.code}</span>}
                </button>
              </li>
            ))}
            {hits.length === 0 && <li className="px-2 py-2 text-sm text-slate-500">No match for “{find.trim()}”</li>}
          </ul>
          {hint}
          {status}
        </div>
      )}
    </div>
  );
}
